import { useEffect, useState } from 'react'

import axios from 'axios'
import { useAbortController } from 'commons/customHooks'
import useParams, { type ParamsType } from 'commons/customHooks/useParams'
import { formatDate, getFirstDayOfMonth } from 'commons/utils/date'
import {
  defaultValues,
  Filter,
  type FilterDefaultValues,
  LoadingHavaianas,
  LoadingModal,
  Page,
  Pagination,
} from 'components'
import { type OriginOrder, YesNoNull } from 'constants/enums'
import type { DraftProps } from 'container/Home/components/Banners/interface'
import _ from 'lodash'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { type BodyRequest, getGerenciarOVListaVddr } from 'services/manage_ov'
import { getComercialStructure } from 'store/ManagementContext/managementSlice'
import type { SapOptionsToOvStatus } from 'store/OVContext/ovContextTypes'
import { resetSalesOrder, setCheckedOvs } from 'store/SalesOrderContext/salesOrderSlice'
import { setViewByFilter } from 'store/StructuralContext'
import { useDispatch, useSelector } from 'store/store'

import ViewByOv from './components/ViewByOv'
import ViewBySalesOrder from './components/ViewBySalesOrder'
import * as S from './styles'
import { type FormatBodyType, formatRequestBodyByFilters } from './utils'

export interface ExecutiveContentProps {
  draftsData: DraftProps[]
}

export enum ViewBy {
  OVS = 'ovs',
  ORDERS = 'orders',
  ORDER_ANTICIPATION = 'orderAnticipation',
}

export enum StatusOVKeys {
  PENDENTE_POR_VENDEDOR = '00- PENDENTE POR VENDEDOR',
  PENDENTE_POR_INDISPONIBILIDADE = '05- PENDENTE POR INDISPONIBILIDADE',
  PENDENTE_ND_VENDEDOR = '08- PENDENTE N.D. VENDEDOR',
  PENDENTE_ND_GRV = '09- PENDENTE N.D. GRV',
  PENDENTE_ND_GNV = '10- PENDENTE N.D. GNV',
  PENDENTE_APROV_OV_ESPECIAL = '15- PENDENTE APROV.OV ESPECIAL',
  PENDENTE_CREDITO_COBRANCA_SAP = '20- PENDENTE CRÉDITO/COBRANÇA-SAP',
  PENDENTE_ERRO_CADASTRO_SAP = '21- PENDENTE ERRO DE CADASTRO-SAP',
  PENDENTE_ARQU_CUSTOMIZACAO_SAP = '22- PENDENTE ARQU.CUSTOMIZAÇÃO-SAP',
  AGUARDANDO_ENVIO_PARA_SAP = '30- AGUARDANDO ENVIO PARA SAP',
  TRANSMITIDO_PARA_O_SAP = '40- TRANSMITIDO PARA O SAP',
  ESTOQUE_INDISPONIVEL_SAP = '45- ESTOQUE INDISPONÍVEL(J0)-SAP',
  AGUARDANDO_RETORNO_DO_SAP = '50- AGUARDANDO RETORNO DO SAP',
  EM_PROCESSO_DE_RESERVA = '60- EM PROCESSO DE RESERVA',
  REMESSA_PARCIAL = '65- REMESSA PARCIAL',
  EM_PROCESSO_DE_REMESSA = '70- EM PROCESSO DE REMESSA',
  FATURADO_PARCIALMENTE = '75- FATURADO PARCIALMENTE',
  FATURADO_TOTALMENTE = '80- FATURADO TOTALMENTE',
  CANCELADO_TOTALMENTE = '90- CANCELADO TOTALMENTE',
}

export type ViewByType = `${ViewBy}`

const ManageOrders = () => {
  const firstPage = 1
  const dispatch = useDispatch()
  const { resetController, isCurrentRequest } = useAbortController()
  const [searchParams] = useSearchParams()
  const { isLoadingOVs } = useSelector(({ salesOrder }) => salesOrder)
  const { viewByFilter } = useSelector((state) => state.structure)
  const { loadingStructure } = useSelector((state) => state.management)
  const { preferences } = useSelector((state) => state.auth)
  const [viewBy, setViewBy] = useState<ViewByType>(viewByFilter)
  const [loading, setIsLoading] = useState(true)
  const [draftsData, setDraftsData] = useState<DraftProps[]>([])
  const [draftsPage, setDraftsPage] = useState(0)
  const [qtPage, setQtPage] = useState(0)
  const [totalDrafts, setTotalDrafts] = useState(0)

  const [ovsSapData, setOvsSapData] = useState<SapOptionsToOvStatus>({} as SapOptionsToOvStatus)
  const { createParamsByObject, getParams } = useParams()

  const PAGE_SIZE = 10

  // NOTE: Create the request Body data to get the orders data
  const getOrdersData = async (body: FormatBodyType = {}) => {
    if (draftsPage > 0) {
      setIsLoading(true)

      const { controller, requestId } = resetController()

      const payload = formatRequestBodyByFilters(body, false, viewBy)

      if (viewBy === ViewBy.OVS) dispatch(setCheckedOvs([]))

      // TODO: Remove
      const selectedViewBy = viewBy === ViewBy.ORDER_ANTICIPATION ? ViewBy.ORDERS : viewBy

      // TODO: Refactor to remove ViewBy.ORDER_ANTICIPATION. Use just viewBy
      await getGerenciarOVListaVddr(
        selectedViewBy,
        {
          Linhas: PAGE_SIZE,
          Paginas: (body.page && body.page - 1) || 0,
        },
        payload as BodyRequest,
        controller.signal,
      )
        .then((response) => {
          if (response.data) {
            setDraftsData(() =>
              response.data.map((res: DraftProps) => ({
                ...res,
                checked: false,
              })),
            )
            setQtPage(Math.ceil(Number(response.data[0].RowsCount) / PAGE_SIZE))
            setTotalDrafts(Number(response.data[0].RowsCount))
          } else {
            setDraftsData([])
            setQtPage(0)
            setTotalDrafts(0)
          }
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            toast.error(`${error?.response?.data}`)
          }
        })
        .finally(() => {
          if (isCurrentRequest(requestId)) {
            setIsLoading(false)
          }
        })
    }
  }

  const priorityFields = [
    'numeroPedidoRascunho',
    'numeroOrdemVenda',
    'numeroOrdemVendaSAP',
    'numeroPedidoCliente',
    'documentoFatura',
    'numeroNotaFiscal',
    'numeroPedidoEDI',
  ]

  useEffect(() => {
    const startDate = getFirstDayOfMonth()
    const endDate = formatDate(new Date())
    const params = getParams()

    const hasPriorityValue = Object.keys(params).some((value) => priorityFields.includes(value))
    const defaultInterval = hasPriorityValue ? '' : `${startDate} - ${endDate}`
    const { CanalDistribuicao: Mercado, RegiaoVenda, EscritorioVenda, EquipeVenda } = preferences

    const payload = {
      ...params,
      codigoZona: params.codigoZona ?? RegiaoVenda ?? '',
      dataCriacaoPedido: viewBy === ViewBy.ORDERS ? params.dataCriacaoPedido : null,
      equipeVenda: params.equipeVenda ?? EquipeVenda ?? '',
      escritorioVenda: params.escritorioVenda ?? EscritorioVenda ?? '',
      mercado: params.mercado ?? Mercado ?? '',
      periodo: params.periodo ?? defaultInterval,
    }
    getOrdersData(payload)
  }, [viewBy])

  useEffect(() => {
    const paramsPage = searchParams.get('page')
    setDraftsPage((oldDraftsPage) => {
      if (paramsPage !== null) {
        if (oldDraftsPage !== +paramsPage) return +paramsPage
      }
      return firstPage
    })
    dispatch(resetSalesOrder())
  }, [])

  useEffect(() => {
    if (draftsPage > 0) {
      const startDate = getFirstDayOfMonth()
      const endDate = formatDate(new Date())
      let newParams = getParams()

      const hasPriorityValue = Object.keys(newParams).some((value) =>
        priorityFields.includes(value),
      )
      const defaultInterval = hasPriorityValue ? '' : `${startDate} - ${endDate}`
      const { CanalDistribuicao: Mercado, RegiaoVenda, EscritorioVenda, EquipeVenda } = preferences

      newParams = {
        ...newParams,
        codigoZona: newParams.codigoZona ?? (hasPriorityValue ? '' : (RegiaoVenda ?? '')),
        equipeVenda: newParams.equipeVenda ?? (hasPriorityValue ? '' : (EquipeVenda ?? '')),
        escritorioVenda:
          newParams.escritorioVenda ?? (hasPriorityValue ? '' : (EscritorioVenda ?? '')),
        mercado: newParams.mercado ?? (hasPriorityValue ? '' : (Mercado ?? '')),
        page: draftsPage.toString(),
        periodo: newParams.periodo ?? defaultInterval,
      }

      getOrdersData(newParams)
      newParams.page = draftsPage.toString()
      createParamsByObject(newParams as unknown as ParamsType)
    }
  }, [draftsPage])

  useEffect(() => {
    dispatch(getComercialStructure())
    const paramsPage = searchParams.get('page')
    if (viewByFilter === ViewBy.OVS) {
      handleViewByChange(ViewBy.OVS, paramsPage || '0')
    }
  }, [])

  const handleFilterTable = (data?: FilterDefaultValues) => {
    const isViewByOrders = viewBy === ViewBy.ORDERS
    const arrayCodStatusOV =
      !isViewByOrders && data?.codigosStatusOrdemVenda
        ? data.codigosStatusOrdemVenda.map((item: string) => +item.split('-')[0])
        : []
    let paramsObject = {}

    if (data) {
      // TODO: Refactor this
      const orderCreationDate =
        isViewByOrders &&
        data.dataCriacaoPedido &&
        !['null', 'Invalid Date'].includes(data.dataCriacaoPedido)
          ? formatDate(data.dataCriacaoPedido, 'YYYY-MM-DDTHH:mm:ss')
          : null

      // TODO: Refactor this and understand why this data is duplicated
      if (
        data.numeroPedidoRascunho !== '' ||
        data.numeroOrdemVenda !== '' ||
        data.numeroOrdemVendaSAP !== '' ||
        data.numeroPedidoCliente !== '' ||
        data.documentoFatura !== '' ||
        data.numeroNotaFiscal !== '' ||
        data.numeroPedidoEDI !== ''
      ) {
        const clearedData = _.omit(data, 'periodo')
        paramsObject = {
          ...clearedData,
          caracteristicas: data?.caracteristicas?.join(','),
          codigosCentros: data?.codigosCentros?.join(','),
          codigosCores: data.codigosCores,
          codigosStatusOrdemVenda: arrayCodStatusOV.join(','),
          codigosStatusPedido: data?.codigosStatusPedido?.join(','),
          codigoZona: data?.codigoZona?.join(','),
          dataCriacaoPedido: orderCreationDate,
          documentoFatura: data.documentoFatura.toLocaleUpperCase(),
          equipeVenda: data.equipeVenda.join(','),
          escritorioVenda: data.escritorioVenda.join(','),
          mercado: data.mercado.join(','),
          numeroOrdemVenda: data.numeroOrdemVenda.toLocaleUpperCase(),
          numeroOrdemVendaSAP: data.numeroOrdemVendaSAP.toLocaleUpperCase(),
          numeroPedidoCliente: data.numeroPedidoCliente.toLocaleUpperCase(),
          numeroPedidoEDI: data.numeroPedidoEDI.toLocaleUpperCase(),
          numeroPedidoRascunho: data.numeroPedidoRascunho.toLocaleUpperCase(),
          origem: data?.origem?.join(',') as OriginOrder,
          page: firstPage,
          searchBox: data.searchBox.toLocaleUpperCase(),
        }
      } else {
        paramsObject = {
          ...data,
          caracteristicas: data?.caracteristicas?.join(','),
          codigosCentros: data?.codigosCentros?.join(','),
          codigosCores: data.codigosCores,
          codigosStatusOrdemVenda: arrayCodStatusOV.join(','),
          codigosStatusPedido: data?.codigosStatusPedido?.join(','),
          codigoZona: data?.codigoZona?.join(','),
          dataCriacaoPedido: orderCreationDate,
          documentoFatura: data.documentoFatura.toLocaleUpperCase(),
          equipeVenda: data.equipeVenda.join(','),
          escritorioVenda: data.escritorioVenda.join(','),
          mercado: data.mercado.join(','),
          numeroOrdemVenda: data.numeroOrdemVenda.toLocaleUpperCase(),
          numeroOrdemVendaSAP: data.numeroOrdemVendaSAP.toLocaleUpperCase(),
          numeroPedidoCliente: data.numeroPedidoCliente.toLocaleUpperCase(),
          numeroPedidoEDI: data.numeroPedidoEDI.toLocaleUpperCase(),
          numeroPedidoRascunho: data.numeroPedidoRascunho.toLocaleUpperCase(),
          origem: data?.origem?.join(',') as OriginOrder,
          page: firstPage,
          searchBox: data.searchBox.toLocaleUpperCase(),
        }
      }
    }
    createParamsByObject(paramsObject as unknown as ParamsType)
    setDraftsPage(firstPage)

    const payload = {
      ...paramsObject,
      codigosCores: data?.codigosCores ? data?.codigosCores.split(',') : [],
      codigosStatusOrdemVenda: arrayCodStatusOV,
      codigosStatusPedido: data?.codigosStatusPedido,
      flagDataFixaEntrega: data?.flagDataFixaEntrega ?? YesNoNull.NULL,
      materialMTO: data?.materialMTO ?? YesNoNull.NULL,
    }
    getOrdersData(payload)
  }

  const handleViewByChange = (value: ViewByType, page?: string) => {
    setIsLoading(true)
    if (!page) {
      setDraftsPage(firstPage)
    }
    setViewBy(value)
    dispatch(setViewByFilter(value))
  }

  const handleClearFilter = () => {
    if (viewBy === ViewBy.OVS) {
      handleViewByChange(ViewBy.ORDERS)
    } else {
      const startDate = getFirstDayOfMonth()
      const endDate = formatDate(new Date())
      const defaultInterval = `${startDate} - ${endDate}`

      setDraftsPage(firstPage)

      const { CanalDistribuicao: Mercado, RegiaoVenda, EscritorioVenda, EquipeVenda } = preferences
      const payload = {
        codigoZona: RegiaoVenda ? [RegiaoVenda] : [],
        equipeVenda: EquipeVenda ? [EquipeVenda] : [],
        escritorioVenda: EscritorioVenda ? [EscritorioVenda] : [],
        mercado: Mercado ? [Mercado] : [],
      }

      const newParams = {
        codigoZona: RegiaoVenda ?? '',
        equipeVenda: EquipeVenda ?? '',
        escritorioVenda: EscritorioVenda ?? '',
        mercado: Mercado ?? '',
      }
      createParamsByObject(newParams as ParamsType)

      getOrdersData({
        ...payload,
        flagDataFixaEntrega: YesNoNull.NULL,
        materialMTO: YesNoNull.NULL,
        page: firstPage,
        periodo: defaultInterval,
      })
    }
  }

  useEffect(() => {
    const params = getParams()
    if (isLoadingOVs) {
      handleFilterTable(defaultValues(params, viewBy))
    }
  }, [isLoadingOVs])

  const handleChangeFilter = () => {
    getOrdersData({ page: draftsPage })
  }

  return (
    <Page fullwidth={true}>
      {loadingStructure ? (
        <LoadingModal />
      ) : (
        <S.Container>
          <Filter
            disabledsButtons={['codigosStatusEstoque']}
            onChangeFilter={handleChangeFilter}
            onClearFilter={handleClearFilter}
            onSubmit={handleFilterTable}
            setViewBy={handleViewByChange}
            viewBy={viewBy}
          />

          <S.ContainerContent>
            {loading ? (
              <LoadingHavaianas />
            ) : (
              <>
                {viewBy === ViewBy.ORDERS && (
                  <ViewBySalesOrder
                    draftsData={draftsData}
                    draftsPage={draftsPage}
                    getOrdersData={getOrdersData}
                    setDraftsData={setDraftsData}
                  />
                )}
                {viewBy === ViewBy.OVS && (
                  <ViewByOv
                    draftsData={draftsData}
                    ovsSapData={ovsSapData}
                    setDraftsData={setDraftsData}
                    setOvsSapData={setOvsSapData}
                  />
                )}
                {totalDrafts > 0 && (
                  <>
                    <S.PaginationWrapper>
                      <Pagination
                        currentPage={draftsPage}
                        itemsPerPage={PAGE_SIZE}
                        numberOfItems={totalDrafts}
                        numberOfPages={qtPage}
                        setCurrentPage={setDraftsPage}
                      />
                    </S.PaginationWrapper>
                  </>
                )}
              </>
            )}
          </S.ContainerContent>
        </S.Container>
      )}
    </Page>
  )
}

export default ManageOrders
